<script>
    import { onMount } from "svelte";

    import { CustomTooltip } from "../../../Components/Common/index.svelte";

    import {
        getAmountInCurrency,
        getYYYYMMDD,
        isObjectEmpty,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        getCollectedPeriodChangeText,
        getEarnedPeriodChangeText,
    } from "../helpers/InfoTextHelper.svelte";

    import LineChart from "../../../Components/Charts/LineChart.svelte";

    import { getDoctorMonthlyInvoicesLineChartData } from "../helpers/GraphPeriodHelper.svelte";

    export let dashboardData = {},
        filters = {};

    let isParsingData = true,
        chartData = [],
        labelArr = [];

    onMount(() => {
        if (dashboardData && !isObjectEmpty(dashboardData))
            syncMonthlyInvoicesData();
        else isParsingData = false;
    });

    const syncMonthlyInvoicesData = () => {
        const syncRes = getDoctorMonthlyInvoicesLineChartData(
            dashboardData.totalEarnedByMonth,
            filters,
        );

        if (syncRes.keys && syncRes.values) {
            const { keys, values } = syncRes;

            keys.map((key, i) =>
                chartData.push({
                    label: [getYYYYMMDD(key.data)],
                    data: [values[i]],
                }),
            );
        }

        isParsingData = false;
    };
</script>

<div class="flex flex-col flex-1 gap-4 p-4">
    <div class="flex flex-1 items-center gap-x-4">
        <div class="flex flex-1 flex-col border rounded-lg p-2">
            <div class="flex flex-1 items-start gap-x-2">
                <div class="flex flex-1 flex-col gap-y-2">
                    <p class="text-md">Net Invoiced</p>
                    <p class="font-bold text-md">
                        {getAmountInCurrency(dashboardData?.totalEarned ?? 0)}
                    </p>
                </div>

                <CustomTooltip zIndex="10">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        class="stroke-current shrink-0 w-4 h-4"
                        slot="tooltipTrigger"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>

                    <div>
                        <p>
                            Net invoiced is the sum of all quotes or invoices
                            that have been sent to Patients, Health Funds and
                            Third parties, minus any refunds in the defined
                            period.
                        </p>

                        <p>
                            Formula = Sum of Quotes from Active Encounters with
                            Status: "Quoted" / "Ready" PLUS Sum of Invoices from
                            Active Encounters with Status: "In Progress" /
                            "Completed" MINUS Sum of Refunds from Active
                            Encounters.
                        </p>
                    </div>
                </CustomTooltip>
            </div>

            <p class="text-sm">
                {getEarnedPeriodChangeText(dashboardData, filters.interval)}
            </p>
        </div>

        <div class="flex flex-1 flex-col border rounded-lg p-2">
            <div class="flex flex-1 items-start gap-x-2">
                <div class="flex flex-1 flex-col gap-y-2">
                    <p class="text-md">Net Received</p>
                    <p class="font-bold text-md">
                        {getAmountInCurrency(
                            dashboardData?.totalCollected ?? 0,
                        )}
                    </p>
                </div>

                <CustomTooltip zIndex="10">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        class="stroke-current shrink-0 w-4 h-4"
                        slot="tooltipTrigger"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>

                    <div>
                        <p>
                            Net received is the sum of all payments received
                            minus any refunds in the defined period.
                        </p>

                        <p>
                            Formula = Sum of Payments Received MINUS Sum of
                            Refunds Confirmed.
                        </p>
                    </div>
                </CustomTooltip>
            </div>

            <p class="text-sm">
                {getCollectedPeriodChangeText(dashboardData, filters.interval)}
            </p>
        </div>
    </div>

    <div class="flex flex-1 flex-col gap-y-2">
        {#if isParsingData}
            <div class="flex flex-1 items-center text-center">
                Parsing chart data
            </div>
        {:else if !chartData.length}
            <div class="flex flex-1 items-center text-center">
                No payments to show
            </div>
        {:else}
            <div class="flex items-center gap-x-2">
                <p class="text-md">Monthly invoices</p>

                <CustomTooltip zIndex="10">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        class="stroke-current shrink-0 w-4 h-4"
                        slot="tooltipTrigger"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>

                    <div>
                        <p>
                            Monthly invoices is the sum of all quotes or
                            invoices that have been sent to Patients, Health
                            Funds and Third parties, minus any refunds that may
                            have occurred each month.
                        </p>

                        <p>
                            Formula = Sum of Quotes from Active Encounters with
                            Status: “Quoted” / “Ready” PLUS Sum of Invoices from
                            Active Encounters with Status: “In Progress” /
                            “Completed” MINUS Sum of Refunds from Active
                            Encounters each month.
                        </p>
                    </div>
                </CustomTooltip>
            </div>

            <LineChart bind:chartData {labelArr} />
        {/if}
    </div>
</div>
